import { connect } from 'formik'
import { flatten } from 'lodash'
import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import { Popover } from '@/components/TailwindUI/Popover'
import { StyledWandIcon, VariableButton } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import messages from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/messages'
import operationsMessages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'
import cn from '@/utilities/cn'

import VariableOverlay from './VariableOverlay'

//<editor-fold desc="Styled Components">
const VariableInputContainer = styled.div`
  position: relative;
  p {
    margin: 5px 0;
  }
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type VariableInputProps = {
  name: string
  required?: boolean
  label?: string
  id?: string
  className?: string
  description?: string
  tooltip?: string
  onChange?: (changedValue: string) => void
  placeholder?: string
  readOnly?: boolean
  variables: Variable[][]
  formik?: any
  multiline?: boolean
  validVariableTypes?: Array<FieldTypes | undefined>
}
//</editor-fold>

const VariableInput = ({
  name,
  required,
  label,
  id,
  placeholder,
  readOnly,
  description,
  tooltip,
  variables,
  className,
  onChange,
  formik,
  multiline = false,
  validVariableTypes
}: VariableInputProps) => {
  const intl = useIntl()

  const addVariableRef = useRef<any>([])
  // @ts-expect-error
  const addSummaryTitleMention = useCallback(addMentionFn => (addVariableRef.current[name] = addMentionFn), [name])
  const [showPopover, setShowPopover] = useState(false)

  const flattenedVariables = flatten(variables)

  return (
    <VariableInputContainer className={cn(className, 'my-3 w-full')}>
      <div className="flex flex-row">
        {label && (
          <label className={`mb-2 block text-sm font-semibold leading-6 text-neutral-600`}>
            {required && <span className="mr-1 text-red-500">*</span>}
            <span>{label}</span>
          </label>
        )}
        {tooltip && <Popover tooltipMessage={tooltip} />}
      </div>
      <div className="flex">
        <div className="flex-grow">
          <div className="flex flex-grow flex-col">
            <MentionsRichTextEditorWithFormik
              className="!mt-0 !mb-0 flex-1"
              readOnly={readOnly}
              description={description}
              placeholder={placeholder ?? ''}
              onChange={onChange}
              setAddMention={addSummaryTitleMention}
              tooltip={tooltip}
              name={name}
              formik={formik}
              multiLine={multiline}
              id={id}
              mentions={getAllFlattenedVariables(flattenedVariables)}
            />
          </div>
        </div>
        {variables.length > 0 && !readOnly && (
          <div className="flex-shrink-0">
            <VariableButton
              className={cn({
                'mt-[29px]': description
              })}
              type={'button'}
              aria-label={
                label
                  ? `${intl.formatMessage(messages.addVariableButton)} ${label}`
                  : intl.formatMessage(messages.addVariableButton)
              }
              onClick={() => setShowPopover(true)}
            >
              <StyledWandIcon title={intl.formatMessage(operationsMessages.addVariableButton)} />
            </VariableButton>
          </div>
        )}
      </div>
      {showPopover && (
        <VariableOverlay
          className="!mt-0"
          onSelectVariable={(variable: Variable) => addVariableRef.current[name](variable)}
          onClose={() => setShowPopover(false)}
          variables={variables}
          validVariableTypes={
            validVariableTypes || [
              FieldTypes.STRING,
              FieldTypes.ARRAY,
              FieldTypes.OBJECT,
              FieldTypes.INTEGER,
              FieldTypes.BOOLEAN,
              FieldTypes.NUMBER,
              FieldTypes.ANY
            ]
          }
        />
      )}
    </VariableInputContainer>
  )
}

VariableInput.displayName = 'VariableInput'

export default connect(VariableInput)
